import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Volume2, Play, Pause, Check, Loader2, Mic, Upload, Trash2 } from 'lucide-react';
import { previewVoice, listCloneVoices, uploadCloneVoice, deleteCloneVoice } from '../../../api/ivrAgents.js';
import { voicePoolFor, isRealtimeVoice, voiceDisplayName, registerCloneVoices, cloneVoiceLabel, FISH_BUILTIN_VOICES } from '../voiceProviders.js';
import { overlayDismiss } from '../../../lib/overlayDismiss';

/**
 * VoicePickerModal — 選擇音色，可輸入文字試聽
 *
 * 流程：
 *   1. 列出可選音色（女聲 / 男聲）
 *   2. 上方 textarea 是試聽文字（預設一句問候）
 *   3. 點音色卡片右側「試聽」→ 後端產 MP3 → 播放
 *   4. 選好後按「套用到此客服」→ sendMessage 觸發 sync-ivr-config + regen greeting
 *
 * 即時互動 2.0（provider=qwen-omni-tts）：音色是複刻聲（Fish model id），清單從後端 GET /api/ivr-flow/voices
 * 拿（內建 18 把中英文名 + 帳號自己的客製音色），可上傳音檔做客製音色（後端截 30 秒 → 去背景音 → 建 model）。
 */

// 音色清單，依風格分類；label / hint 走 i18n（key = voice.<id>.label / .hint）
const VOICES = {
    female: [
        'Aoede', 'Kore', 'Leda', 'Zephyr', 'Achernar', 'Gacrux', 'Despina',
        'Erinome', 'Laomedeia', 'Vindemiatrix', 'Sulafat', 'Pulcherrima', 'Autonoe', 'Callirrhoe',
    ].map(id => ({ id })),
    male: [
        'Puck', 'Charon', 'Fenrir', 'Orus', 'Achird', 'Algenib', 'Algieba', 'Alnilam',
        'Enceladus', 'Iapetus', 'Rasalgethi', 'Sadachbia', 'Sadaltager', 'Schedar', 'Umbriel',
    ].map(id => ({ id })),
    neutral: [
        'Zubenelgenubi',
    ].map(id => ({ id })),
};

export function VoicePickerModal({ open, onClose, onApply, isStreaming, avatarName, avatarId = null, currentVoiceId, currentFirstMessage, newMode = null, provider = 'gemini' }) {
    const { t, i18n } = useTranslation('wsModals');
    const isSurvey = newMode === 'survey';
    const isZh = String(i18n.language || '').toLowerCase().startsWith('zh');
    // 依應答風格切音色池：即時互動(openai，舊資料可能是 grok) 用該家音色、精準應答(gemini) 用原本清單
    const { voices: rtVoices, defaultVoice } = voicePoolFor(provider);
    const isGrok = !!rtVoices;   // 變數名沿用（下方多處引用）：true = realtime provider
    const _p = (provider || 'gemini').toLowerCase();
    const isClone = _p === 'qwen-omni-tts' || _p === 'qwen';   // 即時互動 2.0：複刻聲目錄（後端）
    const [cloneVoices, setCloneVoices] = useState(FISH_BUILTIN_VOICES.map(v => ({ ...v, source: 'builtin' })));
    const [cloneLimits, setCloneLimits] = useState({ minSec: 3, maxSec: 30 });
    const [cloneLoading, setCloneLoading] = useState(false);
    const pool = isClone
        ? {
            female: cloneVoices.filter(v => v.source !== 'custom' && v.gender !== 'male'),
            male: cloneVoices.filter(v => v.source !== 'custom' && v.gender === 'male'),
            neutral: [],
            custom: cloneVoices.filter(v => v.source === 'custom'),
        }
        : rtVoices
            ? { female: rtVoices.female.map(id => ({ id })), male: rtVoices.male.map(id => ({ id })), neutral: [], custom: [] }
            : { ...VOICES, custom: [] };
    const fallbackPreviewText = t('voice.fallbackPreview');
    // realtime 音色沒進 i18n → 用顯示名（Shimmer）；複刻聲 → 「品瑄 · Pinxuan」；gemini 走 i18n
    const voiceLabel = (id) => isClone ? cloneVoiceLabel(id) : isRealtimeVoice(id) ? voiceDisplayName(id) : t(`voice.list.${id}.label`, { defaultValue: id });
    const voiceHint = (id) => {
        if (!isClone) return null;
        const v = cloneVoices.find(x => x.id === id);
        if (!v) return null;
        return (isZh ? v.tagZh : v.tagEn) || v.tagZh || null;
    };
    const [previewText, setPreviewText] = useState(currentFirstMessage || fallbackPreviewText);
    const [selected, setSelected] = useState(currentVoiceId || defaultVoice);
    const [previewingId, setPreviewingId] = useState(null);    // 正在播放的 voiceId
    const [loadingId, setLoadingId] = useState(null);          // 正在打 API 的 voiceId
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const audioRef = useRef(null);
    // 客製音色上傳
    const [uploadOpen, setUploadOpen] = useState(false);
    const [upName, setUpName] = useState('');
    const [upNameEn, setUpNameEn] = useState('');
    const [upGender, setUpGender] = useState('female');
    const [upFile, setUpFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);

    const reloadCloneVoices = async () => {
        setCloneLoading(true);
        try {
            const r = await listCloneVoices();
            const list = Array.isArray(r?.voices) ? r.voices : [];
            if (list.length) { registerCloneVoices(list); setCloneVoices(list); }
            if (r?.limits) setCloneLimits(r.limits);
        } catch (e) {
            setError(t('voice.err.listFail'));
        } finally {
            setCloneLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            setSelected(currentVoiceId || defaultVoice);
            setPreviewText(currentFirstMessage || fallbackPreviewText);
            setError(null);
            setNotice(null);
            setUploadOpen(false);
            setConfirmDeleteId(null);
            if (isClone) reloadCloneVoices();
        } else {
            // 關閉時停掉播放
            if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
            setPreviewingId(null);
            setLoadingId(null);
        }
    }, [open, currentVoiceId]);

    if (!open) return null;

    const playPreview = async (voiceId) => {
        // 點同一個正在播的 → 停
        if (previewingId === voiceId && audioRef.current) {
            audioRef.current.pause();
            audioRef.current = null;
            setPreviewingId(null);
            return;
        }
        // 換 voice → 停舊的
        if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; setPreviewingId(null); }
        if (!previewText.trim()) {
            setError(t('voice.err.noText'));
            return;
        }
        setLoadingId(voiceId);
        setError(null);
        try {
            const { audioUrl } = await previewVoice({ voiceId, text: previewText.trim(), provider });
            const audio = new Audio(audioUrl);
            audio.onended = () => { setPreviewingId(null); audioRef.current = null; };
            audio.onerror = () => { setError(t('voice.err.loadFail')); setPreviewingId(null); audioRef.current = null; };
            audioRef.current = audio;
            await audio.play();
            setPreviewingId(voiceId);
        } catch (e) {
            setError(e.message || t('voice.err.previewFail'));
        } finally {
            setLoadingId(null);
        }
    };

    const handleUpload = async () => {
        if (uploading) return;
        if (!upName.trim()) { setError(t('voice.custom.needName')); return; }
        if (!upFile) { setError(t('voice.custom.needFile')); return; }
        setUploading(true);
        setError(null);
        setNotice(null);
        try {
            const r = await uploadCloneVoice({ file: upFile, nameZh: upName.trim(), nameEn: upNameEn.trim(), gender: upGender, avatarId });
            const v = r?.voice;
            if (v?.id) {
                registerCloneVoices([v]);
                setCloneVoices(prev => [...prev.filter(x => x.id !== v.id), v]);
                setSelected(v.id);
                setNotice(t('voice.custom.done', { name: cloneVoiceLabel(v.id) }) + (v.truncated ? ' ' + t('voice.custom.truncatedNote', { sec: cloneLimits.maxSec }) : ''));
                setUpName(''); setUpNameEn(''); setUpFile(null); setUploadOpen(false);
            }
        } catch (e) {
            setError(e.message || t('voice.err.upload'));
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (id) => {
        if (confirmDeleteId !== id) { setConfirmDeleteId(id); return; }
        setConfirmDeleteId(null);
        try {
            await deleteCloneVoice(id);
            setCloneVoices(prev => prev.filter(x => x.id !== id));
            if (selected === id) setSelected(currentVoiceId || defaultVoice);
            setNotice(t('voice.custom.deleted'));
        } catch (e) {
            setError(e.message || t('voice.err.deleteFail'));
        }
    };

    const handleApply = () => {
        if (isStreaming) return;
        const voice = [...pool.female, ...pool.male, ...pool.neutral, ...pool.custom].find(v => v.id === selected);
        if (!voice) return;
        const newGreeting = previewText.trim();
        const greetingChanged = newGreeting && newGreeting !== (currentFirstMessage || '').trim();
        const voiceChanged = voice.id !== currentVoiceId;

        if (!greetingChanged && !voiceChanged) {
            onClose();
            return;
        }

        const roleLabel = isSurvey ? '問卷 AI 訪員' : '客服';
        const lines = [`請更新這個${roleLabel} (${avatarName || 'avatar'}) 的音色 / 開場白：`, ''];
        if (voiceChanged) {
            lines.push(`- 音色：改成「${voiceLabel(voice.id)}」（voiceId=${voice.id}）`);
        }
        if (greetingChanged) {
            lines.push(`- 開場白：改成「${newGreeting}」`);
        }
        lines.push('');
        lines.push('步驟：');
        if (voiceChanged || greetingChanged) {
            lines.push('1. 用 sync-ivr-config skill 寫回 avatar：');
            if (voiceChanged) {
                if (isClone) {
                    // 即時互動 2.0：音色 = 複刻聲 model id，存 liveConfig.voice + ttsProvider=fish；provider / brainModel 不動
                    lines.push(`   - ivrFlowConfig.liveConfig.voice = ${voice.id}`);
                    lines.push('   - ivrFlowConfig.liveConfig.ttsProvider = fish（保持 ivrFlowConfig.liveConfig.provider = qwen-omni-tts、brainModel 不變，不要改 defaultVoiceId）');
                } else if (isGrok) {
                    // 即時互動(grok)：音色存 liveConfig.voice，保持 provider=grok，不要動 defaultVoiceId
                    lines.push(`   - ivrFlowConfig.liveConfig.voice = ${voice.id}（保持 ivrFlowConfig.liveConfig.provider = ${provider}，不要改 defaultVoiceId）`);
                } else {
                    lines.push(`   - defaultVoiceId = ${voice.id}`);
                }
            }
            if (greetingChanged) {
                lines.push(`   - firstMessage = "${newGreeting}"`);
                if (isSurvey) {
                    // canvas survey:Q1 == greeting,改 firstMessage 同步 canvas entry node + Q1 subcollection doc
                    lines.push(`   - **若 canvas survey 的 entry 題目文字 = 舊 firstMessage** 必須三個地方同時更新到 "${newGreeting}":`);
                    lines.push('     a) `surveyFlows/{avatarId}.canvasFlow.nodes[entry].questionText`(canvas 顯示)');
                    lines.push('     b) `avatars/{avatarId}/surveyQuestions/{entry.questionId}.question`(**SurveyGeminiController runtime 載題目走這條 subcollection**,沒同步 = Live session 第一題會用舊文字)');
                    lines.push('     c) `avatars/{avatarId}.firstMessage`(Web preview Live TTS 用)');
                    lines.push('   - **不要碰** ivrFlowConfig（survey 沒這個 config,但 ivrFlowConfig.greetingMp3 / twilioGreeting 是 MP3 meta、不算業務 config）');
                } else {
                    lines.push(`   - 同時更新 ivrFlowConfig.routerConfig.greeting = "${newGreeting}"（如果有 router 節點）`);
                }
            }
            if (isSurvey) {
                lines.push('2. 用 generate-ivr-greeting skill 重新生成 MP3，**帶 `SURVEY_MODE=force`**（用新音色 + 從 canvas Q1 / firstMessage 讀文字）');
            } else if (isClone) {
                lines.push(`2. 用 generate-ivr-greeting skill 重新生成 MP3：這個角色是即時互動 2.0（provider=qwen-omni-tts、ttsProvider=fish），generate-greeting-standalone.js 會自動用同一把複刻聲 ${voice.id} 合成，**不要指定 Gemini / OpenAI 音色**，否則預錄開場白會跟通話中換配音員`);
            } else if (isGrok) {
                lines.push(`2. 用 generate-ivr-greeting skill 重新生成 MP3，**用 ${provider === 'grok' ? 'Grok' : 'OpenAI'} 引擎 + 音色 ${voice.id} 生成**（provider=${provider}；不要用 Gemini 音色，否則預錄開場白會跟通話中換配音員）`);
            } else {
                lines.push('2. 用 generate-ivr-greeting skill 重新生成 MP3（用新音色 + 新開場白文字）');
            }
        }
        lines.push('完成後告知「音色及開場白 MP3 聲音更新完成」即可，不用列出細節。');

        const dParts = [];
        if (voiceChanged) dParts.push(t('voice.display.voice', { label: voiceLabel(voice.id) }));
        if (greetingChanged) dParts.push(t('voice.display.greeting', { text: newGreeting }));
        const displayText = `${t('voice.display.title')}\n${dParts.join('\n')}`;

        onApply(lines.join('\n'), { displayText });
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-ink-900/30 backdrop-blur-sm z-50 grid place-items-center p-4" {...overlayDismiss(onClose)}>
            <div
                className="bg-cream-50 border border-cream-200 rounded-lg shadow-xl w-full max-w-3xl max-h-[92vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-cream-200">
                    <div className="flex items-center gap-3">
                        <div className="size-9 rounded-md bg-accent-50 flex items-center justify-center">
                            <Volume2 size={18} className="text-accent-700" strokeWidth={1.7} />
                        </div>
                        <div>
                            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-400">voice & greeting</div>
                            <h2 className="heading-display text-lg font-semibold text-ink-900 mt-0.5">{t('voice.title')}</h2>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="size-8 inline-flex items-center justify-center rounded-md text-ink-400 hover:text-ink-700 hover:bg-cream-100 transition"
                    >
                        <X size={16} />
                    </button>
                </div>

                {/* 開場白文字（套用後會更新 avatar 的開場白並重生 MP3） */}
                <div className="px-6 pt-4 pb-3 border-b border-cream-200">
                    <div className="flex items-center justify-between mb-1.5">
                        <div className="font-mono text-[10px] uppercase tracking-wider text-ink-500">{t('voice.greetingTextLabel')}</div>
                        <div className="text-[10px] text-ink-400">{t('voice.greetingTextHint')}</div>
                    </div>
                    <textarea
                        value={previewText}
                        onChange={(e) => setPreviewText(e.target.value)}
                        rows={2}
                        placeholder={t('voice.greetingPlaceholder')}
                        className="w-full resize-none px-3 py-2 text-sm border border-cream-300 focus:border-accent-500 outline-none rounded-md bg-white"
                    />
                    {error && (
                        <div className="mt-2 text-xs text-danger-500 bg-danger-100/40 border border-danger-500/30 rounded px-2 py-1">
                            {error}
                        </div>
                    )}
                    {notice && !error && (
                        <div className="mt-2 text-xs text-accent-700 bg-accent-50 border border-accent-200 rounded px-2 py-1">
                            {notice}
                        </div>
                    )}
                </div>

                {/* Voice 列表 */}
                <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
                    {isClone && cloneLoading && (
                        <div className="text-xs text-ink-400 flex items-center gap-1.5"><Loader2 size={12} className="animate-spin" /> {t('voice.loadingVoices')}</div>
                    )}
                    {isClone && (
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <div className="font-mono text-[10px] uppercase tracking-wider text-ink-400">
                                    <Mic size={11} className="inline mr-1 mb-0.5" /> {t('voice.group.custom')}
                                </div>
                                <button
                                    onClick={() => { setUploadOpen(v => !v); setError(null); }}
                                    className="text-xs px-2.5 py-1 rounded-md border border-cream-300 text-ink-700 hover:border-accent-400 hover:text-accent-700 transition flex items-center gap-1"
                                >
                                    <Upload size={12} /> {t('voice.custom.title')}
                                </button>
                            </div>
                            {uploadOpen && (
                                <div className="mb-3 p-3 rounded-md border border-dashed border-accent-300 bg-accent-50/30 space-y-2">
                                    <div className="text-[11px] text-ink-500">{t('voice.custom.hint', { min: cloneLimits.minSec, max: cloneLimits.maxSec })}</div>
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                        <input
                                            value={upName}
                                            onChange={(e) => setUpName(e.target.value)}
                                            maxLength={30}
                                            placeholder={t('voice.custom.nameZh')}
                                            className="px-2.5 py-1.5 text-sm border border-cream-300 focus:border-accent-500 outline-none rounded-md bg-white"
                                        />
                                        <input
                                            value={upNameEn}
                                            onChange={(e) => setUpNameEn(e.target.value)}
                                            maxLength={30}
                                            placeholder={t('voice.custom.nameEn')}
                                            className="px-2.5 py-1.5 text-sm border border-cream-300 focus:border-accent-500 outline-none rounded-md bg-white"
                                        />
                                        <select
                                            value={upGender}
                                            onChange={(e) => setUpGender(e.target.value)}
                                            className="px-2.5 py-1.5 text-sm border border-cream-300 focus:border-accent-500 outline-none rounded-md bg-white"
                                        >
                                            <option value="female">{t('voice.group.female')}</option>
                                            <option value="male">{t('voice.group.male')}</option>
                                        </select>
                                    </div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <input
                                            type="file"
                                            accept="audio/*,video/mp4,.m4a,.mp3,.wav,.webm,.ogg,.flac,.aac"
                                            onChange={(e) => setUpFile(e.target.files?.[0] || null)}
                                            className="text-xs text-ink-600 file:mr-2 file:px-2.5 file:py-1 file:rounded-md file:border file:border-cream-300 file:bg-white file:text-xs file:text-ink-700"
                                        />
                                        <button
                                            onClick={handleUpload}
                                            disabled={uploading || !upFile || !upName.trim()}
                                            className="ml-auto px-3 py-1.5 text-xs rounded-md bg-ink-900 text-white hover:bg-ink-700 transition disabled:bg-cream-300 disabled:cursor-not-allowed flex items-center gap-1.5"
                                        >
                                            {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                                            {uploading ? t('voice.custom.uploading') : t('voice.custom.upload')}
                                        </button>
                                    </div>
                                </div>
                            )}
                            {pool.custom.length > 0 ? (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    {pool.custom.map((v) => (
                                        <VoiceCard
                                            key={v.id}
                                            voice={v}
                                            labelFn={voiceLabel}
                                            hintFn={voiceHint}
                                            selected={selected === v.id}
                                            playing={previewingId === v.id}
                                            loading={loadingId === v.id}
                                            onSelect={() => setSelected(v.id)}
                                            onPreview={() => playPreview(v.id)}
                                            onDelete={() => handleDelete(v.id)}
                                            confirmDelete={confirmDeleteId === v.id}
                                        />
                                    ))}
                                </div>
                            ) : (
                                !uploadOpen && <div className="text-[11px] text-ink-400">{t('voice.custom.empty')}</div>
                            )}
                        </div>
                    )}
                    <VoiceGroup
                        title={t('voice.group.female')}
                        voices={pool.female}
                        labelFn={voiceLabel}
                        hintFn={voiceHint}
                        selected={selected}
                        previewingId={previewingId}
                        loadingId={loadingId}
                        onSelect={setSelected}
                        onPreview={playPreview}
                    />
                    <VoiceGroup
                        title={t('voice.group.male')}
                        voices={pool.male}
                        labelFn={voiceLabel}
                        hintFn={voiceHint}
                        selected={selected}
                        previewingId={previewingId}
                        loadingId={loadingId}
                        onSelect={setSelected}
                        onPreview={playPreview}
                    />
                    {pool.neutral.length > 0 && (
                        <VoiceGroup
                            title={t('voice.group.neutral')}
                            voices={pool.neutral}
                            labelFn={voiceLabel}
                            selected={selected}
                            previewingId={previewingId}
                            loadingId={loadingId}
                            onSelect={setSelected}
                            onPreview={playPreview}
                        />
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-6 py-3 border-t border-cream-200 bg-cream-50">
                    <div className="text-xs text-ink-500">
                        {t('voice.selected')}<span className="text-ink-900 font-medium">
                            {voiceLabel(selected)}
                        </span>
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-sm rounded-md text-ink-700 hover:bg-cream-100 transition"
                        >
                            {t('common:actions.cancel')}
                        </button>
                        <button
                            onClick={handleApply}
                            disabled={
                                isStreaming
                                || !selected
                                || (selected === currentVoiceId && previewText.trim() === (currentFirstMessage || '').trim())
                            }
                            className="px-4 py-2 text-sm rounded-md bg-ink-900 text-white hover:bg-ink-700 transition disabled:bg-cream-300 disabled:cursor-not-allowed flex items-center gap-1.5"
                        >
                            <Check size={14} /> {t('voice.applyBtn')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function VoiceGroup({ title, voices, labelFn, hintFn, selected, previewingId, loadingId, onSelect, onPreview }) {
    return (
        <div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-ink-400 mb-2">
                <Mic size={11} className="inline mr-1 mb-0.5" /> {title}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {voices.map((v) => (
                    <VoiceCard
                        key={v.id}
                        voice={v}
                        labelFn={labelFn}
                        hintFn={hintFn}
                        selected={selected === v.id}
                        playing={previewingId === v.id}
                        loading={loadingId === v.id}
                        onSelect={() => onSelect(v.id)}
                        onPreview={() => onPreview(v.id)}
                    />
                ))}
            </div>
        </div>
    );
}

function VoiceCard({ voice, labelFn, hintFn, selected, playing, loading, onSelect, onPreview, onDelete = null, confirmDelete = false }) {
    const { t } = useTranslation('wsModals');
    return (
        <div
            onClick={onSelect}
            className={[
                'flex items-center gap-3 px-3 py-2.5 rounded-md border cursor-pointer transition group',
                selected
                    ? 'border-accent-500 bg-accent-50/50 ring-1 ring-accent-300'
                    : 'border-cream-200 bg-white hover:border-accent-300',
            ].join(' ')}
        >
            <div className={[
                'size-9 rounded-md flex items-center justify-center shrink-0',
                selected ? 'bg-accent-500 text-white' : 'bg-cream-100 text-ink-500 group-hover:bg-accent-100 group-hover:text-accent-700',
            ].join(' ')}>
                {selected ? <Check size={16} strokeWidth={2.2} /> : <Volume2 size={16} strokeWidth={1.7} />}
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-ink-900 truncate">{labelFn ? labelFn(voice.id) : t(`voice.list.${voice.id}.label`, { defaultValue: voice.id })}</div>
                {(() => {
                    // 複刻聲：hint = 目錄的中/英標籤；realtime(openai/grok) 沒 i18n 條目 → 不顯示（避免印出原始 key）
                    const custom = hintFn ? hintFn(voice.id) : null;
                    if (custom) return <div className="text-[11px] text-ink-400 truncate">{custom}</div>;
                    if (isRealtimeVoice(voice.id)) return null;
                    const hint = t(`voice.list.${voice.id}.hint`, { defaultValue: '' });
                    return hint ? <div className="text-[11px] text-ink-400 truncate">{hint}</div> : null;
                })()}
            </div>
            {onDelete && (
                <button
                    onClick={(e) => { e.stopPropagation(); onDelete(); }}
                    title={confirmDelete ? t('voice.custom.confirmDelete') : t('voice.custom.delete')}
                    className={[
                        'h-8 rounded-md flex items-center justify-center transition shrink-0 text-[11px]',
                        confirmDelete ? 'px-2 bg-danger-500 text-white' : 'size-8 bg-cream-100 text-ink-400 hover:bg-danger-100 hover:text-danger-500',
                    ].join(' ')}
                >
                    {confirmDelete ? t('voice.custom.confirmDelete') : <Trash2 size={13} />}
                </button>
            )}
            <button
                onClick={(e) => { e.stopPropagation(); onPreview(); }}
                disabled={loading}
                title={t('voice.previewBtn')}
                className={[
                    'size-8 rounded-md flex items-center justify-center transition shrink-0',
                    loading
                        ? 'bg-cream-200 text-ink-400 cursor-wait'
                        : playing
                        ? 'bg-accent-500 text-white'
                        : 'bg-cream-100 text-ink-500 hover:bg-accent-500 hover:text-white',
                ].join(' ')}
            >
                {loading ? <Loader2 size={14} className="animate-spin" /> : playing ? <Pause size={14} /> : <Play size={14} />}
            </button>
        </div>
    );
}
